<template>
    <Detail v-if="data" show-footer>
        <template v-slot:header>
            <div class="row">
                <div class="col col-6">
                    <h4>{{ $t("buyer") }}</h4>
                    <h2>{{ data.name }}</h2>
                    <h3>{{ $filters.upper($t("organization_id")) }}: {{ data.ico }}</h3>
                </div>
                <div id="suppliers" class="col col-2">
                    <h4>{{ $t("supplier", 2) }}</h4>
                    <h2 class="numeric">
                        {{ formatInteger(data.suppliers_count) }}
                    </h2>
                </div>
                <div id="period" class="col col-4">
                    <h4>{{ $t("period") }}</h4>
                    <h2 class="numeric">
                        {{ $filters.formatDate(data.start_date, "M/YYYY") }}-{{ $filters.formatDate(data.end_date, "M/YYYY")
                        }}
                    </h2>
                </div>
            </div>
        </template>

        <template v-slot:header_small>
            <div class="row no-gutters">
                <div class="col col-8">
                    <h2>{{ data.name }}</h2>
                </div>
                <div id="period" class="col col-4">
                    <h2 class="numeric">
                        {{ $filters.formatDate(data.start_date, "M/YYYY") }} - {{
                            $filters.formatDate(data.end_date, "M/YYYY")
                        }}
                    </h2>
                </div>
            </div>
        </template>

        <template v-slot:menu>
            <Menu :nav="nav" current="risks" />
        </template>

        <template v-slot:content>
            <section v-if="data" id="risks_section">
                <color-bubble variant="dark" ratio="half">
                    <template v-slot:icon>
                        <b-icon icon="arrow-up" />
                    </template>
                    <template v-slot:heading>
                        {{ $t("overview.risk.short", [$t("risk.level." + data.risk_level_lite)]) }}
                    </template>
                    <template v-slot:description>
                        {{ $t("overview.risk.long", { save: formatNumber(data.save) }) }}
                    </template>
                </color-bubble>

                <color-bubble variant="beige" ratio="half" class="color_dark_important">
                    <template v-slot:icon>
                        <b-icon icon="hash" />
                    </template>
                    <template v-slot:heading>{{
                        $t("overview.supplier.short", data.suppliers_risky_count, [
                            formatInteger(data.suppliers_risky_count)
                        ])
                    }}</template>
                    <template v-slot:description>{{
                        $t("overview.supplier.long", [
                            formatInteger(data.suppliers_count),
                            formatInteger(data.suppliers_risky_count)
                        ])
                    }}</template>
                </color-bubble>

                <color-bubble variant="beige" ratio="half" class="color_dark_important">
                    <template v-slot:icon>
                        <b-icon icon="wallet" />
                    </template>
                    <template v-slot:heading>{{
                        $t("overview.sum.short", [formatNumber(data.suppliers_total)])
                    }}</template>
                    <template v-slot:description>{{ $t("overview.sum.long") }}</template>
                </color-bubble>

                <h5 class="mt-5 mb-3">{{ $t("overview.flags.title") }}</h5>

                <RisksTable :flags="data.flags" :version="version" :clickable-rows="true"
                    @row-clicked="(f) => pushFilter({ flag: [{ name: f.name, type: f.type }] })"></RisksTable>

                <color-bubble variant="orange" @click="order(data.name, data.ico, 'full')" class="clickable-color-bubble">
                    <template v-slot:icon>
                        <b-icon icon="star-fill"/>
                    </template>
                    <template v-slot:heading>{{
                        $t("overview.flags.shareOnSpendings")
                    }}</template>
                    <template v-slot:description>
                        {{ $t("button.order") }}&nbsp;
                        <b-icon icon="arrow-right"/>
                    </template>
                </color-bubble>
            </section>
        </template>
    </Detail>
</template>

<script>
import Detail from "@/views/layouts/Detail.vue";
import RisksTable from "@/components/RisksTable.vue";
import Menu from "@/components/Menu.vue";
import ColorBubble from "@/components/ColorBubble.vue";
import formattersMixin from "@/plugins/formattersMixin.js";
import analysisMixin from "@/plugins/analysisMixin.js";

export default {
    props: {
        hash: { required: true }
    },
    name: "lite",
    components: { Detail, RisksTable, Menu, ColorBubble },
    mixins: [formattersMixin, analysisMixin],
    mounted: function () {
        this.isBusy = true;
        this.loadData()
            .then(() => {
                this.$store.dispatch("setCurrentAnalysis", this.data)
                this.$root.$emit("set-page-title", this.$t("titleDetail", { name: this.data.name }))
            })
            .catch((e) => console.log(e))
            .finally((this.isBusy = false));
    },
    computed: {
        version: () => "lite"
    },
    data: function () {
        return {
            data: null,
            isBusy: false,
            nav: {
                risks: {
                    to: { name: this.version, params: { hash: this.hash } },
                    label: this.$t("nav.risks")
                },
                suppliers: {
                    to: {
                        name: "suppliers",
                        params: { hash: this.hash, version: "lite" }
                    },
                    label: this.$t("nav.suppliers")
                },
                tenders: {
                    to: {
                        name: "tenders",
                        params: { hash: this.hash, version: "lite" }
                    },
                    label: this.$t("nav.tenders")
                },
                contracts: {
                    to: {
                        name: "contracts",
                        params: { hash: this.hash, version: "lite" }
                    },
                    label: this.$t("nav.contracts")
                }
            },
            currentNav: "risks"
        };
    },
    methods: {
        loadData: function () {
            return new Promise((resolve, reject) => {
                var self = this;

                var params = {
                    format: "json",
                    lite_hash: this.hash
                };

                this.$store.getters.api
                    .get(`${this.version}/${this.hash}`, { params: params })
                    .then((response) => {
                        self.data = response.data;
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                        self.data = null;
                    });
            });
        },
        pushFilter: function (filter) {
            if (filter.flag[0].type === "contract") {
                this.$store.commit("setContractTableFilter", filter || {});
                this.$router.push({
                    name: "contracts",
                    params: { hash: this.hash, version: this.version }
                });
            }
            else if (filter.flag[0].type === "tender") {
                this.$store.commit("setTenderTableFilter", filter || {});
                this.$router.push({
                    name: "tenders",
                    params: { hash: this.hash, version: this.version }
                });
            }
            else {
                this.$store.commit("setSupplierTableFilter", filter || {});
                this.$router.push({
                    name: "suppliers",
                    params: { hash: this.hash, version: this.version }
                });
            }
        }
    }
};
</script>

<style scoped lang="scss">
@use "@/scss/custom";

.premium_column {
    margin-top: 23px;
    padding-left: 10px;
}

#menu {
    margin-bottom: 50px;

    button {
        text-align: left;
        background-color: white;
        padding: 0.6rem 1.5rem;
        padding-right: 0;

        &.active {
            background-color: custom.$background-light;
        }

        &:hover {
            background-color: custom.$light;
        }

        &[disabled] {
            background-color: transparent;
        }

        .premium_label {
            float: right;
        }
    }
}

.risk_table {
    margin-bottom: 30px;
}
</style>
